// Billing settings page — current plan card, trial countdown, usage, and a
// "Manage plan" handoff to Shopify's hosted plan page.
//
// Under Shopify App Pricing, Shopify hosts plan selection, change, and cancel —
// we don't do those in-app. So this page is read-only status (from GET
// /api/billing/status, sourced from the Partner-API mirror) plus a button that
// opens Shopify's hosted page in the top frame. See docs/APP-PRICING-MIGRATION.md.
//
// State-specific UI:
//   comped                       : "free during beta" card, no plan controls
//   no subscription, free period : days left + end date, "Choose a plan"
//   no subscription, period over : "Delivery paused" card, "Choose a plan"
//                                  (review W31)
//   trial                        — plan card + "X days left in trial" + usage bar
//   active                       — plan card + usage bar + "Manage plan"
//   cancelled                    — plan card + "Active through {periodEnd}" notice
//   declined / expired / frozen  — plan card + warning + "Choose a plan"

import { useEffect, useState } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Badge,
  Spinner,
  ProgressBar,
  Divider,
  Box,
} from "@shopify/polaris";
import { apiFetch, ApiError, getCached } from "./api";
import {
  freePeriodEnded,
  openPlanSelection as openHostedPlanPage,
  type EntitlementView,
  type FreePeriodView,
} from "./billing-shared";
import { daysUntil, formatDate } from "./dates";

// Mirrors src/routes/billing.ts GET /api/billing/status response.
interface BillingStatus {
  // True when the shop is comped for the beta — full access, never charged.
  comped?: boolean;
  // Shopify's hosted plan-selection URL (admin.shopify.com/.../pricing_plans).
  planSelectionUrl?: string;
  entitlement?: EntitlementView;
  // The 30 free days of a store with no plan (review W31); null otherwise.
  freePeriod?: FreePeriodView | null;
  subscription: {
    plan: "flex" | "standard" | "pro";
    billingCycle: "monthly" | "usage";
    status: "pending" | "trial" | "active" | "cancelled" | "frozen" | "declined" | "expired";
    trialEndsAt: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelledAt: string | null;
    tier: {
      displayName: string;
      monthlyBase: number;
      includedOrders: number;
      perOrderRate: number;
    };
    usage: {
      orderCount: number;
      overageCount: number;
      overageBilled: number;
      periodStart: string;
      periodEnd: string;
    } | null;
  } | null;
}

interface BillingProps {
  shop: string;
  onBack?: () => void;
}

export function Billing({ shop, onBack }: BillingProps) {
  const shopify = useAppBridge();
  // Seed from the SWR cache (App's bootstrap already fetched billing status) so
  // this page paints instantly; reload() revalidates in the background.
  const [status, setStatus] = useState<BillingStatus | null>(
    () => getCached<BillingStatus>("/api/billing/status") ?? null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const resp = await apiFetch<BillingStatus>(shopify, "/api/billing/status");
      setStatus(resp);
      setLoadError(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setLoadError(msg);
    }
  };

  useEffect(() => {
    void reload();
    // shopify is stable per App Bridge contract; reload identity isn't a dep here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open Shopify's hosted plan page in the top frame.
  const openPlanSelection = () => openHostedPlanPage(status?.planSelectionUrl);

  if (loadError) {
    return (
      <Page title="Billing" subtitle={shop} backAction={onBack ? { content: "Back", onAction: onBack } : undefined}>
        <Banner tone="critical" title="Couldn't load billing status">
          <p>{loadError}</p>
          <p>
            <Button onClick={() => void reload()}>Retry</Button>
          </p>
        </Banner>
      </Page>
    );
  }

  if (status === null) {
    return (
      <Page title="Billing" subtitle={shop} backAction={onBack ? { content: "Back", onAction: onBack } : undefined}>
        <Card>
          <BlockStack gap="200" align="center">
            <Spinner accessibilityLabel="Loading billing status" size="large" />
            <Text as="p" tone="subdued">Loading…</Text>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  // Comped beta store — full access, no plan needed, never charged.
  if (status.comped) {
    return (
      <Page title="Billing" subtitle={shop} backAction={onBack ? { content: "Back", onAction: onBack } : undefined}>
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h2" variant="headingMd">You're on the Datacrow beta</Text>
              <Badge tone="success">Free</Badge>
            </InlineStack>
            <Text as="p" tone="subdued">
              Everything's unlocked and you won't be charged during the beta, so there's
              no plan to pick. We'll reach out before the beta ends with a thank-you offer.
            </Text>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  // No subscription yet: the free period's state (review W31), then a hand-off
  // to the hosted plan page.
  if (status.subscription === null) {
    const ended = freePeriodEnded(status);
    const fp = status.freePeriod ?? null;
    const days = fp ? daysUntil(fp.endsAt) : null;
    return (
      <Page title="Billing" subtitle={shop} backAction={onBack ? { content: "Back", onAction: onBack } : undefined}>
        <Card>
          <BlockStack gap="300">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h2" variant="headingMd">{ended ? "Free period ended" : "No plan yet"}</Text>
              {ended && <Badge tone="warning">Delivery paused</Badge>}
            </InlineStack>
            <Text as="p" tone="subdued">
              {ended
                ? `Datacrow stopped sending events to your ad platforms${fp ? ` on ${formatDate(fp.endsAt)}` : ""}. ` +
                  "It still records them. Pick a plan to start sending again; events from the paused days are not sent later."
                : fp && days !== null
                  ? `Datacrow is free until ${formatDate(fp.endsAt)} ` +
                    `(${days <= 1 ? "less than a day" : `${days} days`} left). After that, it stops sending ` +
                    "events to your ad platforms until you pick a plan. Standard and Pro start with a 30-day free trial."
                  : "Pick a plan to keep events flowing to your ad platforms. Standard and Pro start with a 30-day free trial."}
            </Text>
            <InlineStack>
              <Button variant="primary" onClick={openPlanSelection} disabled={!status.planSelectionUrl}>
                Choose a plan
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  const sub = status.subscription;
  const isActiveOrTrial = sub.status === "active" || sub.status === "trial";
  const manageLabel = isActiveOrTrial ? "Manage plan" : "Choose a plan";

  return (
    <Page title="Billing" subtitle={shop} backAction={onBack ? { content: "Back", onAction: onBack } : undefined}>
      <BlockStack gap="400">
        <StatusBanner sub={sub} />

        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h2" variant="headingLg">{sub.tier.displayName}</Text>
              <StatusBadge status={sub.status} />
            </InlineStack>

            <PriceLine sub={sub} />

            <Divider />

            <PeriodInfo sub={sub} />

            {sub.usage !== null && (
              <>
                <Divider />
                <UsageBlock sub={sub} />
              </>
            )}

            <Divider />

            <BlockStack gap="100">
              <InlineStack gap="200">
                <Button variant="primary" onClick={openPlanSelection} disabled={!status.planSelectionUrl}>
                  {manageLabel}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued" variant="bodySm">
                Change your plan or cancel anytime on Shopify's billing page.
              </Text>
            </BlockStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

// ---------- Sub-components ----------

function StatusBanner({ sub }: { sub: NonNullable<BillingStatus["subscription"]> }) {
  if (sub.status === "pending") {
    return (
      <Banner tone="info" title="Awaiting Shopify approval">
        <p>
          You started a subscription but haven't approved the charge in Shopify yet.
          Check the Shopify admin's billing page.
        </p>
      </Banner>
    );
  }
  if (sub.status === "trial" && sub.trialEndsAt) {
    const days = daysUntil(sub.trialEndsAt);
    return (
      <Banner tone={days <= 3 ? "warning" : "info"}>
        <p>
          <strong>
            {days <= 0
              ? "Trial ends today."
              : days === 1
              ? "1 day left in your trial."
              : `${days} days left in your trial.`}
          </strong>
          {" "}Your card will be charged on {formatDate(sub.trialEndsAt)}.
        </p>
      </Banner>
    );
  }
  if (sub.status === "cancelled") {
    const periodEnded = new Date(sub.currentPeriodEnd) <= new Date();
    if (periodEnded) {
      return (
        <Banner tone="warning" title="Subscription ended">
          <p>
            Your billing period ended after the cancellation. Event ingestion is still
            recording, but delivery is paused until you re-subscribe.
          </p>
        </Banner>
      );
    }
    return (
      <Banner tone="warning" title="Subscription cancelled">
        <p>
          Events will keep being delivered through{" "}
          <strong>{formatDate(sub.currentPeriodEnd)}</strong>. After that, delivery
          pauses until you re-subscribe.
        </p>
      </Banner>
    );
  }
  if (sub.status === "declined") {
    return (
      <Banner tone="critical" title="Subscription declined">
        <p>The charge was declined in Shopify. Choose a plan to resume delivery.</p>
      </Banner>
    );
  }
  if (sub.status === "frozen") {
    return (
      <Banner tone="critical" title="Subscription frozen">
        <p>
          Shopify froze your subscription (typically a payment-method issue). Update
          your billing in Shopify admin to resume.
        </p>
      </Banner>
    );
  }
  if (sub.status === "expired") {
    return (
      <Banner tone="warning" title="Subscription expired">
        <p>Choose a plan to resume server-side event delivery.</p>
      </Banner>
    );
  }
  return null;
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "trial":
      return <Badge tone="info">Trial</Badge>;
    case "active":
      return <Badge tone="success">Active</Badge>;
    case "pending":
      return <Badge tone="attention">Pending approval</Badge>;
    case "cancelled":
      return <Badge tone="warning">Cancelled</Badge>;
    case "declined":
      return <Badge tone="critical">Declined</Badge>;
    case "frozen":
      return <Badge tone="critical">Frozen</Badge>;
    case "expired":
      return <Badge tone="warning">Expired</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

function PriceLine({ sub }: { sub: NonNullable<BillingStatus["subscription"]> }) {
  if (sub.plan === "flex") {
    return (
      <Text as="p">
        <strong>${sub.tier.perOrderRate.toFixed(2)}</strong> per order, billed via Shopify.
      </Text>
    );
  }
  return (
    <Text as="p">
      <strong>${sub.tier.monthlyBase}</strong> per month.{" "}
      {sub.tier.includedOrders.toLocaleString()} orders included, then $
      {sub.tier.perOrderRate.toFixed(2)} per additional order.
    </Text>
  );
}

function PeriodInfo({ sub }: { sub: NonNullable<BillingStatus["subscription"]> }) {
  return (
    <InlineStack gap="600">
      <Box>
        <Text as="p" tone="subdued" variant="bodySm">Current period</Text>
        <Text as="p">
          {formatDate(sub.currentPeriodStart)} – {formatDate(sub.currentPeriodEnd)}
        </Text>
      </Box>
      <Box>
        <Text as="p" tone="subdued" variant="bodySm">Billing</Text>
        <Text as="p" tone="base">{sub.plan === "flex" ? "Pay per order" : "Monthly"}</Text>
      </Box>
    </InlineStack>
  );
}

function UsageBlock({ sub }: { sub: NonNullable<BillingStatus["subscription"]> }) {
  const orderCount = sub.usage?.orderCount ?? 0;
  const overage = sub.usage?.overageCount ?? 0;
  const billed = sub.usage?.overageBilled ?? 0;
  const cap = sub.tier.includedOrders;
  const rawPct = cap > 0 ? (orderCount / cap) * 100 : 0;
  // Floor nonzero usage at a 1% sliver: an empty bar under a nonzero count
  // reads as "not tracking" (19/5,000 rounded to a literal 0 before).
  const pctOfCap = orderCount > 0 ? Math.min(100, Math.max(1, rawPct)) : 0;
  const tone = pctOfCap >= 100 ? "critical" : pctOfCap >= 80 ? "highlight" : "primary";
  const projection = projectedBill(sub);

  return (
    <BlockStack gap="200">
      <InlineStack align="space-between">
        <Text as="p" tone="subdued" variant="bodySm">This period's usage</Text>
        <Text as="p" tone="subdued" variant="bodySm">
          {cap > 0
            ? `${orderCount.toLocaleString()} / ${cap.toLocaleString()} orders`
            : `${orderCount.toLocaleString()} orders`}
        </Text>
      </InlineStack>
      {cap > 0 && <ProgressBar progress={pctOfCap} tone={tone} size="medium" />}
      {cap > 0 && overage > 0 && (
        <Text as="p" tone="subdued" variant="bodySm">
          <strong>{overage.toLocaleString()}</strong> overage orders this period
          {billed > 0 ? ` (${formatMoney(billed)} billed via Shopify)` : ""}.
        </Text>
      )}
      {projection !== null && (
        <Text as="p" tone="subdued" variant="bodySm">
          Projected this period: <strong>{formatMoney(projection)}</strong> based on{" "}
          {orderCount.toLocaleString()} orders so far.
        </Text>
      )}
    </BlockStack>
  );
}

// ---------- Formatting helpers ----------
// formatDate and daysUntil live in dates.ts (shared with the free-period UI).

const DAY_MS = 24 * 60 * 60 * 1000;

// Straight-line end-of-period bill estimate (launch-copy C-P4): the pace so
// far extrapolated across the full period, priced on the current tier. Only
// shown for states where this period actually bills; a trial period charges
// nothing, so projecting a dollar figure there would be false.
function projectedBill(sub: NonNullable<BillingStatus["subscription"]>): number | null {
  if (sub.status !== "active" && sub.status !== "cancelled") return null;
  if (!sub.usage) return null;
  const start = new Date(sub.usage.periodStart).getTime();
  const end = new Date(sub.usage.periodEnd).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const periodDays = (end - start) / DAY_MS;
  // Clamp elapsed to [1 day, full period]: a minutes-old period would explode
  // the extrapolation, and a finished period should project the actuals.
  const elapsedDays = Math.min(periodDays, Math.max(1, (Date.now() - start) / DAY_MS));
  const projectedOrders = Math.round((sub.usage.orderCount / elapsedDays) * periodDays);
  const projectedOverage = Math.max(0, projectedOrders - sub.tier.includedOrders);
  return sub.tier.monthlyBase + projectedOverage * sub.tier.perOrderRate;
}

function formatMoney(usd: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(usd);
}
